/**
 * TanStack Query hooks for the Social module.
 *
 * Phase 1 scaffolding: only the read-side hooks land here. Mutations
 * (connect/disconnect account, create post, schedule post, publish-now)
 * arrive once the Meta OAuth Edge Function is wired in Phase 1.1.
 *
 * RLS does the heavy lifting — the org-scoping happens at the database
 * layer via public.get_user_org_ids(). The hooks just specify the
 * organization_id filter for query-key cohesion and to keep the network
 * payload tight (PostgREST will refuse rows from other orgs anyway).
 */

import { keepPreviousData, useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import type { SupabaseClient } from "@supabase/supabase-js";
import { supabase } from "@/lib/supabase";
import { StatusChangedConcurrentlyError } from "@/lib/concurrent-change";
import { useOrganization } from "@/contexts/OrganizationContext";
import type {
  Engagement,
  Post,
  PostContentType,
  PostMedia,
  PostStatus,
  PostTarget,
  SocialAccount,
  SocialPlatform,
} from "./types";
import type { MetricPoint } from "./insights/metric-trend";
import { resolveComposeAction, type ComposeAction } from "@shared/social-approval";

// ---------------------------------------------------------------------------
// Field selectors
// ---------------------------------------------------------------------------

// Note the explicit non-selection of access_token / refresh_token. Encrypted
// ciphertext belongs in Edge Functions only — never hand it to the browser
// even in encrypted form.
const SOCIAL_ACCOUNT_FIELDS = `
  id, organization_id, platform, platform_account_id,
  account_name, account_display_name, account_avatar_url, account_type,
  parent_account_id, is_active, last_synced_at, metadata, scopes,
  token_expires_at, created_at, updated_at
`;

const POST_FIELDS = `
  id, organization_id, created_by, campaign_id,
  title, body, status, content_type,
  ai_generated, ai_prompt,
  scheduled_at, published_at, tags,
  created_at, updated_at, deleted_at
`;

const POST_FIELDS_WITH_TARGETS = `
  ${POST_FIELDS},
  targets:post_targets (
    id, post_id, social_account_id, organization_id,
    platform_post_id, platform_post_url, status,
    published_at, failure_reason, retry_count,
    created_at, updated_at,
    social_account:social_accounts ( id, platform, account_name, account_avatar_url )
  ),
  media:post_media ( id, storage_path, public_url, media_type, alt_text, sort_order )
`;

// The social tables are still represented by hand-maintained UI models while
// generated Relay schema types catch up. Keep that boundary explicit and
// avoid weakening the rest of this module with `any` casts.
const relayFrom = (table: string) =>
  (supabase as unknown as SupabaseClient).schema("relay").from(table);

// ---------------------------------------------------------------------------
// Social accounts
// ---------------------------------------------------------------------------

interface SocialAccountsFilter {
  platform?: SocialPlatform;
  activeOnly?: boolean;
}

export function useSocialAccounts(filter: SocialAccountsFilter = {}) {
  const { organizationId } = useOrganization();

  return useQuery({
    queryKey: ["social-accounts", "list", organizationId, filter],
    queryFn: async (): Promise<SocialAccount[]> => {
      if (!organizationId) return [];

      let query = supabase
        .from("social_accounts")
        .select(SOCIAL_ACCOUNT_FIELDS)
        .eq("organization_id", organizationId)
        .order("created_at", { ascending: false });

      if (filter.platform) query = query.eq("platform", filter.platform);
      if (filter.activeOnly) query = query.eq("is_active", true);

      const { data, error } = await query.limit(100);
      if (error) throw error;
      return (data ?? []) as SocialAccount[];
    },
    enabled: !!organizationId,
    placeholderData: keepPreviousData,
    staleTime: 60 * 1000,
  });
}

export function useSocialAccount(accountId: string | undefined) {
  const { organizationId } = useOrganization();

  return useQuery({
    queryKey: ["social-accounts", "detail", accountId],
    queryFn: async (): Promise<SocialAccount | null> => {
      if (!accountId || !organizationId) return null;
      const { data, error } = await supabase
        .from("social_accounts")
        .select(SOCIAL_ACCOUNT_FIELDS)
        .eq("id", accountId)
        .eq("organization_id", organizationId)
        .maybeSingle();
      if (error) throw error;
      return (data as SocialAccount | null) ?? null;
    },
    enabled: !!accountId && !!organizationId,
    staleTime: 60 * 1000,
  });
}

// ---------------------------------------------------------------------------
// Posts
// ---------------------------------------------------------------------------

interface PostsFilter {
  status?: PostStatus | "all";
  campaignId?: string;
  withTargets?: boolean;
}

type PostWithRelationShape = Omit<Post, "targets" | "media"> & {
  targets?: PostTarget | PostTarget[] | null;
  media?: PostMedia | PostMedia[] | null;
};

function normalizePost(raw: PostWithRelationShape): Post {
  // Supabase relational selects return joined arrays/objects depending on
  // cardinality. Normalize to always-array on `targets` and `media`.
  const targets = Array.isArray(raw.targets)
    ? raw.targets
    : raw.targets
      ? [raw.targets]
      : [];
  const media = Array.isArray(raw.media)
    ? raw.media
    : raw.media
      ? [raw.media]
      : [];
  return { ...raw, targets, media };
}

// Shared by both branches of usePosts()'s query (see the comment at its call
// site for why the branch can't be collapsed back into a single ternary select).
function applyPostFilters<
  T extends {
    eq(column: string, value: unknown): T;
    is(column: string, value: null): T;
    order(column: string, options?: { ascending?: boolean }): T;
  },
>(query: T, organizationId: string, filter: PostsFilter): T {
  let q = query
    .eq("organization_id", organizationId)
    .is("deleted_at", null)
    .order("created_at", { ascending: false });

  if (filter.status && filter.status !== "all") {
    q = q.eq("status", filter.status);
  }
  if (filter.campaignId) {
    q = q.eq("campaign_id", filter.campaignId);
  }
  return q;
}

export function usePosts(filter: PostsFilter = {}) {
  const { organizationId } = useOrganization();

  return useQuery({
    queryKey: ["posts", "list", organizationId, filter],
    queryFn: async (): Promise<Post[]> => {
      if (!organizationId) return [];

      // .select() must be called with a single literal field-selector string --
      // passing the ternary directly gives it a union of the two constants,
      // which trips a real @supabase/postgrest-js compile-time query-parser
      // limitation (it can't parse a union Query type and produces a bogus
      // ParserError type instead of the row shape). Branching the whole chain
      // keeps each call site's Query argument a single literal.
      const { data, error } = filter.withTargets
        ? await applyPostFilters(
            supabase.from("posts").select(POST_FIELDS_WITH_TARGETS),
            organizationId,
            filter
          ).limit(500)
        : await applyPostFilters(
            supabase.from("posts").select(POST_FIELDS),
            organizationId,
            filter
          ).limit(500);
      if (error) throw error;
      return (data ?? []).map((raw) => normalizePost(raw as PostWithRelationShape));
    },
    enabled: !!organizationId,
    placeholderData: keepPreviousData,
    staleTime: 30 * 1000,
  });
}

export function usePost(postId: string | undefined) {
  const { organizationId } = useOrganization();

  return useQuery({
    queryKey: ["posts", "detail", postId],
    queryFn: async (): Promise<Post | null> => {
      if (!postId || !organizationId) return null;
      const { data, error } = await supabase
        .from("posts")
        .select(POST_FIELDS_WITH_TARGETS)
        .eq("id", postId)
        .eq("organization_id", organizationId)
        .is("deleted_at", null)
        .maybeSingle();
      if (error) throw error;
      return data ? normalizePost(data as PostWithRelationShape) : null;
    },
    enabled: !!postId && !!organizationId,
    staleTime: 30 * 1000,
  });
}

/**
 * Header KPI: count of connected platforms (distinct).
 * Used by the social-accounts settings page and dashboard widgets.
 */
export function useConnectedPlatformCount(): number {
  const { data: accounts = [] } = useSocialAccounts({ activeOnly: true });
  return new Set(accounts.map((a) => a.platform)).size;
}

// ---------------------------------------------------------------------------
// Post metrics (read-side analytics — S2 seam)
// ---------------------------------------------------------------------------

export interface PostMetricSnapshot {
  impressions: number | null;
  reach: number | null;
  engagement: number | null;
  clicks: number | null;
  captured_at: string;
}

/**
 * Reads the latest `relay.post_metrics` snapshot per post target.
 *
 * The table is EMPTY in S1 — metrics only start landing in S2, once Meta
 * approves `read_insights` and live publishing is enabled. So this resolves to
 * `{}` today; the insights UI renders an honest empty state until then.
 *
 * Returns a map keyed by post_target_id → the most recent snapshot (by
 * captured_at desc). Targets with no rows are simply absent from the map.
 */
export function usePostMetrics(postTargetIds: string[]) {
  const { organizationId } = useOrganization();

  return useQuery({
    queryKey: ["post-metrics", organizationId, postTargetIds],
    queryFn: async (): Promise<Record<string, PostMetricSnapshot | undefined>> => {
      if (!organizationId || postTargetIds.length === 0) return {};

      const { data, error } = await supabase
        .from("post_metrics" as never)
        .select("post_target_id, impressions, reach, engagement, clicks, captured_at")
        .eq("organization_id", organizationId)
        .in("post_target_id", postTargetIds)
        .order("captured_at", { ascending: false });
      if (error) throw error;

      // Rows are captured_at desc; the first row seen per target is the latest.
      const latest: Record<string, PostMetricSnapshot | undefined> = {};
      for (const row of (data ?? []) as unknown as Array<{
        post_target_id: string;
        impressions: number | null;
        reach: number | null;
        engagement: number | null;
        clicks: number | null;
        captured_at: string;
      }>) {
        if (!latest[row.post_target_id]) {
          latest[row.post_target_id] = {
            impressions: row.impressions,
            reach: row.reach,
            engagement: row.engagement,
            clicks: row.clicks,
            captured_at: row.captured_at,
          };
        }
      }
      return latest;
    },
    enabled: !!organizationId && postTargetIds.length > 0,
    staleTime: 60 * 1000,
  });
}

/**
 * Reads the `relay.post_metrics` time series per target over the last `days`
 * (default 30), oldest → newest. Powers the S2.x sparkline trends. Returns `{}`
 * when the table is empty (gated / no data yet). Complements usePostMetrics
 * (latest snapshot per target).
 */
export function usePostMetricsHistory(postTargetIds: string[], days = 30) {
  const { organizationId } = useOrganization();

  return useQuery({
    queryKey: ["post-metrics-history", organizationId, postTargetIds, days],
    queryFn: async (): Promise<Record<string, MetricPoint[]>> => {
      if (!organizationId || postTargetIds.length === 0) return {};
      const since = new Date(Date.now() - days * 24 * 60 * 60 * 1000).toISOString();

      const { data, error } = await supabase
        .from("post_metrics" as never)
        .select("post_target_id, impressions, reach, engagement, clicks, captured_at")
        .eq("organization_id", organizationId)
        .in("post_target_id", postTargetIds)
        .gte("captured_at", since)
        .order("captured_at", { ascending: true });
      if (error) throw error;

      const byTarget: Record<string, MetricPoint[]> = {};
      for (const row of (data ?? []) as unknown as Array<{
        post_target_id: string;
        impressions: number | null;
        reach: number | null;
        engagement: number | null;
        clicks: number | null;
        captured_at: string;
      }>) {
        (byTarget[row.post_target_id] ??= []).push({
          captured_at: row.captured_at,
          impressions: row.impressions,
          reach: row.reach,
          engagement: row.engagement,
          clicks: row.clicks,
        });
      }
      return byTarget;
    },
    enabled: !!organizationId && postTargetIds.length > 0,
    staleTime: 60 * 1000,
  });
}

// ---------------------------------------------------------------------------
// Mutations
// ---------------------------------------------------------------------------

interface MetaAuthorizeResponse {
  authorization_url: string;
  state: string;
}

/**
 * Kicks off the Meta/Facebook OAuth flow.
 *
 * Invokes relay-social-meta-authorize → gets authorization_url → redirects
 * the browser to Facebook Login. No query invalidation needed because the
 * browser is navigating away.
 *
 * redirect_uri is centralized here so it is byte-identical to the value
 * passed to useCompleteMetaCallback (Meta requires exact match).
 */
export function useConnectMeta() {
  const { organizationId } = useOrganization();

  return useMutation({
    mutationFn: async (): Promise<MetaAuthorizeResponse> => {
      if (!organizationId) throw new Error("No active organization");
      const redirectUri = `${window.location.origin}/auth/callback/meta`;
      const { data, error } = await supabase.functions.invoke<MetaAuthorizeResponse>(
        "relay-social-meta-authorize",
        { body: { organization_id: organizationId, redirect_uri: redirectUri } },
      );
      if (error) throw error;
      if (!data?.authorization_url) {
        throw new Error("relay-social-meta-authorize returned no authorization URL");
      }
      return data;
    },
    onSuccess: (data) => {
      window.location.assign(data.authorization_url);
    },
  });
}

/**
 * Soft-disconnects a social account (sets is_active = false).
 *
 * When the target is a Facebook Page (parent), its linked Instagram child
 * accounts are ALSO deactivated in the same operation to avoid orphaned
 * children that can no longer post.
 *
 * Schema access: plain `.from("social_accounts")` — matches the convention
 * in the read hooks above (the Supabase client is already scoped to the
 * relay schema; see src/lib/supabase.ts).
 *
 * Invalidates ["social-accounts"] on success so the UI refreshes.
 */
export function useDisconnectAccount() {
  const qc = useQueryClient();
  const { organizationId } = useOrganization();

  return useMutation({
    mutationFn: async (accountId: string): Promise<void> => {
      if (!organizationId) throw new Error("No active organization");

      // Deactivate the target account.
      const { error: parentErr } = await supabase
        .from("social_accounts")
        .update({ is_active: false })
        .eq("id", accountId)
        .eq("organization_id", organizationId);
      if (parentErr) throw parentErr;

      // Cascade to any IG (or other) children linked via parent_account_id.
      // Two separate update calls keeps each query simple and avoids building
      // a complex .or() across columns of different semantics.
      const { error: childErr } = await supabase
        .from("social_accounts")
        .update({ is_active: false })
        .eq("parent_account_id", accountId)
        .eq("organization_id", organizationId);
      if (childErr) throw childErr;
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["social-accounts"] });
    },
  });
}

// ---------------------------------------------------------------------------
// Social settings
// ---------------------------------------------------------------------------

interface SocialSettings {
  require_approval: boolean;
}

/**
 * Reads the per-org social_settings row (require_approval toggle).
 * Returns { require_approval: false } if no row exists yet.
 */
export function useSocialSettings() {
  const { organizationId } = useOrganization();

  return useQuery({
    queryKey: ["social-settings", organizationId],
    queryFn: async (): Promise<SocialSettings> => {
      if (!organizationId) return { require_approval: false };
      const { data, error } = await supabase
        .from("social_settings" as never)
        .select("require_approval")
        .eq("organization_id", organizationId)
        .maybeSingle();
      if (error) throw error;
      return (data as SocialSettings | null) ?? { require_approval: false };
    },
    enabled: !!organizationId,
    staleTime: 60 * 1000,
  });
}

// ---------------------------------------------------------------------------
// Post mutations
// ---------------------------------------------------------------------------

interface CreatePostInput {
  body: string;
  title?: string | null;
  content_type?: PostContentType;
  scheduled_at?: string | null;
  status?: PostStatus;
}

/**
 * Inserts a new post row for the active org and current user.
 * Invalidates ["posts"] on success.
 */
export function useCreatePost() {
  const qc = useQueryClient();
  const { organizationId } = useOrganization();

  return useMutation({
    mutationFn: async (input: CreatePostInput): Promise<Post> => {
      if (!organizationId) throw new Error("No active organization");
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) throw new Error("Not authenticated");

      const { data, error } = await relayFrom("posts")
        .insert({
          organization_id: organizationId,
          created_by: user.id,
          body: input.body,
          title: input.title ?? null,
          content_type: input.content_type ?? "text",
          scheduled_at: input.scheduled_at ?? null,
          status: input.status ?? "draft",
        } as unknown as never)
        .select(POST_FIELDS)
        .single();
      if (error) throw error;
      return normalizePost(data);
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["posts"] });
    },
  });
}

interface UpdatePostInput {
  id: string;
  patch: Partial<{
    body: string;
    title: string | null;
    content_type: PostContentType;
    scheduled_at: string | null;
    status: PostStatus;
    ai_generated: boolean;
    ai_prompt: string | null;
  }>;
}

/**
 * Patches an existing post. Invalidates list + detail query keys.
 */
export function useUpdatePost() {
  const qc = useQueryClient();
  const { organizationId } = useOrganization();

  return useMutation({
    mutationFn: async ({ id, patch }: UpdatePostInput): Promise<Post> => {
      if (!organizationId) throw new Error("No active organization");
      const { data, error } = await relayFrom("posts")
        .update(patch as unknown as never)
        .eq("id", id)
        .eq("organization_id", organizationId)
        .select(POST_FIELDS)
        .single();
      if (error) throw error;
      return normalizePost(data);
    },
    onSuccess: (_data, { id }) => {
      qc.invalidateQueries({ queryKey: ["posts"] });
      qc.invalidateQueries({ queryKey: ["posts", "detail", id] });
    },
  });
}

// ---------------------------------------------------------------------------
// Post media mutations
// ---------------------------------------------------------------------------

interface UploadPostMediaInput {
  postId: string;
  file: File;
  sortOrder?: number;
  altText?: string;
}

/**
 * Uploads a file to the relay-social-media bucket and inserts a post_media row.
 * Invalidates ["posts","detail",postId] on success.
 */
export function useUploadPostMedia() {
  const qc = useQueryClient();
  const { organizationId } = useOrganization();

  return useMutation({
    mutationFn: async ({ postId, file, sortOrder, altText }: UploadPostMediaInput): Promise<PostMedia> => {
      if (!organizationId) throw new Error("No active organization");

      const path = `${organizationId}/${postId}/${crypto.randomUUID()}-${file.name}`;
      const { error: uploadError } = await supabase.storage
        .from("relay-social-media")
        .upload(path, file, { upsert: false });
      if (uploadError) throw uploadError;

      const { data, error } = await relayFrom("post_media")
        .insert({
          post_id: postId,
          organization_id: organizationId,
          storage_path: path,
          media_type: file.type.startsWith("video") ? "video" : "image",
          file_name: file.name,
          file_size_bytes: file.size,
          sort_order: sortOrder ?? 0,
          alt_text: altText ?? null,
        } as unknown as never)
        .select()
        .single();
      if (error) throw error;
      return data as PostMedia;
    },
    onSuccess: (_data, { postId }) => {
      qc.invalidateQueries({ queryKey: ["posts", "detail", postId] });
    },
  });
}

interface UpdatePostMediaInput {
  id: string;
  postId: string;
  patch: { alt_text?: string | null; sort_order?: number };
}

/**
 * Patches a post_media row (alt_text and/or sort_order), org-scoped.
 * Invalidates ["posts","detail",postId] on success.
 */
export function useUpdatePostMedia() {
  const qc = useQueryClient();
  const { organizationId } = useOrganization();

  return useMutation({
    mutationFn: async ({ id, patch }: UpdatePostMediaInput): Promise<void> => {
      if (!organizationId) throw new Error("No active organization");

      const { error } = await supabase
        .from("post_media" as never)
        .update(patch as never)
        .eq("id", id)
        .eq("organization_id", organizationId);
      if (error) throw error;
    },
    onSuccess: (_data, { postId }) => {
      qc.invalidateQueries({ queryKey: ["posts", "detail", postId] });
    },
  });
}

interface RemovePostMediaInput {
  id: string;
  postId: string;
  storagePath: string;
}

/**
 * Removes a media file from storage and deletes its post_media row.
 * Invalidates ["posts","detail",postId] on success.
 */
export function useRemovePostMedia() {
  const qc = useQueryClient();
  const { organizationId } = useOrganization();

  return useMutation({
    mutationFn: async ({ id, postId: _postId, storagePath }: RemovePostMediaInput): Promise<void> => {
      if (!organizationId) throw new Error("No active organization");

      const { error: storageError } = await supabase.storage
        .from("relay-social-media")
        .remove([storagePath]);
      if (storageError) throw storageError;

      const { error } = await supabase
        .from("post_media" as never)
        .delete()
        .eq("id", id)
        .eq("organization_id", organizationId);
      if (error) throw error;
    },
    onSuccess: (_data, { postId }) => {
      qc.invalidateQueries({ queryKey: ["posts", "detail", postId] });
    },
  });
}

/**
 * Generates signed URLs for a list of storage paths in the relay-social-media
 * bucket. Returns a Record mapping storagePath → signedUrl.
 * staleTime is set below the 1-hour expiry to avoid serving expired URLs.
 */
export function usePostMediaSignedUrls(paths: string[]) {
  return useQuery({
    queryKey: ["social-media-signed", paths],
    queryFn: async (): Promise<Record<string, string>> => {
      const { data, error } = await supabase.storage
        .from("relay-social-media")
        .createSignedUrls(paths, 3600);
      if (error) throw error;
      const map: Record<string, string> = {};
      for (const item of data ?? []) {
        if (item.path && item.signedUrl) {
          map[item.path] = item.signedUrl;
        }
      }
      return map;
    },
    enabled: paths.length > 0,
    staleTime: 50 * 60 * 1000,
  });
}

// ---------------------------------------------------------------------------
// Post targets mutation
// ---------------------------------------------------------------------------

interface SetPostTargetsInput {
  postId: string;
  socialAccountIds: string[];
}

/**
 * Atomically replaces post_targets for a post: deletes existing rows then
 * inserts one row per socialAccountId. Invalidates ["posts","detail",postId].
 */
export function useSetPostTargets() {
  const qc = useQueryClient();
  const { organizationId } = useOrganization();

  return useMutation({
    mutationFn: async ({ postId, socialAccountIds }: SetPostTargetsInput): Promise<void> => {
      if (!organizationId) throw new Error("No active organization");

      const { error: deleteError } = await supabase
        .from("post_targets" as never)
        .delete()
        .eq("post_id", postId)
        .eq("organization_id", organizationId);
      if (deleteError) throw deleteError;

      if (socialAccountIds.length > 0) {
        const rows = socialAccountIds.map((social_account_id) => ({
          post_id: postId,
          social_account_id,
          organization_id: organizationId,
          status: "pending",
        }));
        const { error: insertError } = await supabase
          .from("post_targets" as never)
          // eslint-disable-next-line @typescript-eslint/no-explicit-any
          .insert(rows as any);
        if (insertError) throw insertError;
      }
    },
    onSuccess: (_data, { postId }) => {
      qc.invalidateQueries({ queryKey: ["posts", "detail", postId] });
    },
  });
}

// ---------------------------------------------------------------------------
// Post status transition
// ---------------------------------------------------------------------------

interface TransitionPostInput {
  id: string;
  currentStatus: PostStatus;
  action: ComposeAction;
  requireApproval: boolean;
}

/**
 * Drives the compose-time approval state machine (Task 13).
 * Calls resolveComposeAction; throws if the transition is invalid; otherwise
 * patches posts.status to the resolved next status.
 * Invalidates ["posts"] and ["posts","detail",id].
 */
export function useTransitionPost() {
  const qc = useQueryClient();
  const { organizationId } = useOrganization();

  return useMutation({
    mutationFn: async ({ id, currentStatus, action, requireApproval }: TransitionPostInput): Promise<Post> => {
      if (!organizationId) throw new Error("No active organization");

      const result = resolveComposeAction(currentStatus, action, requireApproval);
      if (!result.ok) throw new Error(result.error);

      // CAS on the caller-supplied currentStatus. Two reviewers approving and
      // rejecting the same post concurrently would each pass resolveComposeAction
      // against their stale cache and clobber each other silently. With the
      // guard the loser throws StatusChangedConcurrentlyError so the UI can
      // surface a refresh-prompt instead of a confusing "rejected then
      // immediately approved" state.
      const { data, error } = await supabase
        .from("posts" as never)
        .update({ status: result.next } as unknown as never)
        .eq("id", id)
        .eq("organization_id", organizationId)
        .eq("status", currentStatus)
        .select(POST_FIELDS)
        .maybeSingle();
      if (error) throw error;
      if (!data) throw new StatusChangedConcurrentlyError("post");
      return normalizePost(data);
    },
    onSuccess: (_data, { id }) => {
      qc.invalidateQueries({ queryKey: ["posts"] });
      qc.invalidateQueries({ queryKey: ["posts", "detail", id] });
    },
    onError: (_e, { id }) => {
      // Same invalidation set as onSuccess so a CAS miss (or any other
      // error) snaps the UI to reality on the next render. Without this,
      // ComposeActionBar's catch shows a toast but the stale `status` prop
      // stays in cache — a reviewer who lost an approve/reject race can
      // keep retrying the same losing transition until a manual refresh.
      // Mirrors the onError pattern in useUpdateProjectStatus +
      // useToggleCustomAutomation.
      qc.invalidateQueries({ queryKey: ["posts"] });
      qc.invalidateQueries({ queryKey: ["posts", "detail", id] });
    },
  });
}

// ---------------------------------------------------------------------------
// Schedule (enqueue publish job)
// ---------------------------------------------------------------------------

interface SchedulePostInput {
  post_id: string;
  scheduled_at?: string | null;
}

interface SchedulePostResponse {
  enqueued: boolean;
  already?: boolean;
  run_at: string;
}

/**
 * Schedules a post for publishing via the relay-social-schedule-post Edge
 * Function: it validates targets, flips the post to `scheduled`, and enqueues
 * the PUBLISH_POST job (idempotency_key publish:<post_id>). Pass scheduled_at
 * to publish later; omit (or null) to publish now.
 *
 * Live posting stays gated by SOCIAL_PUBLISH_ENABLED on the server side, so a
 * scheduled job simply holds at `scheduled` until the flag is on.
 *
 * Invalidates ["posts"] + ["posts","detail",post_id] on success.
 */
export function useSchedulePost() {
  const qc = useQueryClient();

  return useMutation({
    mutationFn: async ({ post_id, scheduled_at }: SchedulePostInput): Promise<SchedulePostResponse> => {
      const { data, error } = await supabase.functions.invoke<SchedulePostResponse>(
        "relay-social-schedule-post",
        { body: { post_id, scheduled_at: scheduled_at ?? null } },
      );
      if (error) throw error;
      if (!data?.enqueued) {
        throw new Error("relay-social-schedule-post did not enqueue the post");
      }
      return data;
    },
    onSuccess: (_data, { post_id }) => {
      qc.invalidateQueries({ queryKey: ["posts"] });
      qc.invalidateQueries({ queryKey: ["posts", "detail", post_id] });
    },
  });
}

// ---------------------------------------------------------------------------
// Refresh metrics (enqueue an on-demand analytics sync)
// ---------------------------------------------------------------------------

interface RefreshMetricsResponse {
  enqueued: number;
  flag_off?: boolean;
}

/**
 * Enqueues an immediate SYNC_SOCIAL_ANALYTICS job per published target via the
 * relay-social-refresh-metrics Edge Function (hour-granular idempotency).
 *
 * The function is flag-gated server-side (SOCIAL_ANALYTICS_ENABLED): when off it
 * returns { enqueued: 0, flag_off: true } without touching the Graph API.
 *
 * Invalidates ["post-metrics"] immediately, then schedules a short polling
 * window because writes happen asynchronously in relay-job-runner.
 */
export function useRefreshMetrics() {
  const qc = useQueryClient();
  const invalidateMetricQueries = () => {
    qc.invalidateQueries({ queryKey: ["post-metrics"] });
    qc.invalidateQueries({ queryKey: ["post-metrics-history"] });
  };

  return useMutation({
    mutationFn: async (postId: string): Promise<RefreshMetricsResponse> => {
      const { data, error } = await supabase.functions.invoke<RefreshMetricsResponse>(
        "relay-social-refresh-metrics",
        { body: { post_id: postId } },
      );
      if (error) throw error;
      return data as RefreshMetricsResponse;
    },
    onSuccess: () => {
      invalidateMetricQueries();

      const pollIntervalMs = 3_000;
      const pollAttempts = 5;
      for (let attempt = 1; attempt <= pollAttempts; attempt += 1) {
        window.setTimeout(() => {
          invalidateMetricQueries();
        }, pollIntervalMs * attempt);
      }
    },
  });
}

// ---------------------------------------------------------------------------
// Engagements (E sub-project)
// ---------------------------------------------------------------------------

const ENGAGEMENT_FIELDS = `
  id, organization_id, post_target_id, social_account_id,
  platform, engagement_type, platform_user_id, platform_username,
  platform_display_name, platform_avatar_url, content,
  platform_engagement_id, contact_id, crm_converted_at,
  occurred_at, created_at,
  contact:contacts ( id, first_name, last_name ),
  post_target:post_targets (
    id, platform_post_url,
    post:posts ( id, title, body )
  )
`;

interface EngagementsFilter {
  unconvertedOnly?: boolean;
  platform?: "instagram" | "facebook";
  limit?: number;
  offset?: number;
}

export function useEngagements(filter: EngagementsFilter = {}) {
  const { organizationId } = useOrganization();
  return useQuery({
    queryKey: ["social-engagements", "list", organizationId, filter],
    queryFn: async (): Promise<Engagement[]> => {
      if (!organizationId) return [];
      let q = relayFrom("social_engagements")
        .select(ENGAGEMENT_FIELDS)
        .eq("organization_id", organizationId)
        .order("occurred_at", { ascending: false })
        .limit(filter.limit ?? 50);
      if (filter.unconvertedOnly) q = q.is("contact_id", null);
      if (filter.platform) q = q.eq("platform", filter.platform);
      if (typeof filter.offset === "number") {
        q = q.range(filter.offset, filter.offset + (filter.limit ?? 50) - 1);
      }
      const { data, error } = await q;
      if (error) throw error;
      // relayFrom() drops the Database generic (see its definition above), so
      // postgrest-js can't see the contact_id -> contacts.id FK metadata and
      // defaults the `contact` embed to array cardinality even though it's a
      // to-one relation at runtime. Route through `unknown` per TS's own
      // suggestion rather than widening relayFrom's typing repo-wide here.
      return (data ?? []) as unknown as Engagement[];
    },
    enabled: !!organizationId,
  });
}

export function useUnlinkedEngagementCount() {
  const { organizationId } = useOrganization();
  return useQuery({
    queryKey: ["social-engagements", "unlinked-count", organizationId],
    queryFn: async (): Promise<number> => {
      if (!organizationId) return 0;
      const { count, error } = await relayFrom("social_engagements")
        // GET (not HEAD) count — avoids the edge 503 on HEAD count probes
        // (see notifications/api.ts useUnreadNotificationCount). limit(1)
        // returns the exact count via Content-Range without fetching rows.
        .select("id", { count: "exact" })
        .eq("organization_id", organizationId)
        .is("contact_id", null)
        .limit(1);
      if (error) throw error;
      return count ?? 0;
    },
    enabled: !!organizationId,
    staleTime: 30_000,
  });
}

export function useLinkEngagement() {
  const qc = useQueryClient();
  const { organizationId } = useOrganization();
  return useMutation({
    mutationFn: async ({
      engagementId,
      contactId,
    }: {
      engagementId: string;
      contactId: string | null;
    }) => {
      const { error } = await supabase
        .from("social_engagements" as never)
        .update({
          contact_id: contactId,
          crm_converted_at: contactId ? new Date().toISOString() : null,
        } as never)
        .eq("id", engagementId)
        .eq("organization_id", organizationId!);
      if (error) throw error;
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["social-engagements"] });
    },
  });
}

// ---------------------------------------------------------------------------
// AI caption generation
// ---------------------------------------------------------------------------
export interface GenerateCaptionInput {
  prompt: string;
  platforms: string[];
  tone?: string;
  org_name?: string;
  existing_body?: string;
}

export interface GenerateCaptionResponse {
  caption: string;
  platform_notes?: string;
  tokens_used?: number;
}

/**
 * Calls the relay-ai-caption Edge Function to generate or rewrite a social
 * media caption.  Pass existing_body to trigger rewrite mode.
 *
 * Rate-limited server-side (api profile). The caller should surface errors
 * via toast.
 */
export function useGenerateCaption() {
  return useMutation({
    mutationFn: async (input: GenerateCaptionInput): Promise<GenerateCaptionResponse> => {
      const { data, error } = await supabase.functions.invoke<GenerateCaptionResponse>(
        "relay-ai-caption",
        { body: input },
      );
      if (error) throw error;
      if (!data?.caption) throw new Error("relay-ai-caption returned no caption");
      return data;
    },
  });
}

// ---------------------------------------------------------------------------
// AI optimal posting time
// ---------------------------------------------------------------------------
export interface OptimalTimeRecommendation {
  platform:    string;
  local_hour:  number;
  day_of_week: number;
  confidence:  "high" | "medium" | "low";
  data_points: number;
}

export interface OptimalTimeResponse {
  recommendations: OptimalTimeRecommendation[];
}

/**
 * Calls relay-ai-optimal-time to get the best posting times per platform.
 *
 * Falls back to industry benchmarks (Sprout Social / Hootsuite 2024) when the
 * org has fewer than 10 post_metrics rows.  No Anthropic call — pure analytics.
 *
 * Usage:
 *   const optimalTime = useOptimalPostingTime();
 *   const result = await optimalTime.mutateAsync(["instagram", "facebook"]);
 */
export function useOptimalPostingTime() {
  const { organizationId } = useOrganization();
  return useMutation({
    mutationFn: async (platforms: string[]): Promise<OptimalTimeResponse> => {
      if (!organizationId) throw new Error("No active organization");
      const { data, error } = await supabase.functions.invoke<OptimalTimeResponse>(
        "relay-ai-optimal-time",
        { body: { organization_id: organizationId, platforms } },
      );
      if (error) throw error;
      if (!data?.recommendations?.length) {
        throw new Error("relay-ai-optimal-time returned no recommendations");
      }
      return data;
    },
  });
}

// ---------------------------------------------------------------------------
// LinkedIn OAuth
// ---------------------------------------------------------------------------
interface LinkedInAuthorizeResponse {
  authorization_url: string;
  state: string;
}

/**
 * Kicks off the LinkedIn OAuth flow for personal profiles.
 *
 * Invokes relay-social-linkedin-authorize → gets authorization_url →
 * redirects the browser to LinkedIn Login. Mirrors useConnectMeta.
 *
 * redirect_uri is centralized here so it is byte-identical to the value
 * sent from AuthCallbackLinkedIn (LinkedIn requires exact match).
 */
export function useConnectLinkedIn() {
  const { organizationId } = useOrganization();
  return useMutation({
    mutationFn: async (): Promise<LinkedInAuthorizeResponse> => {
      if (!organizationId) throw new Error("No active organization");
      const redirectUri = `${window.location.origin}/auth/callback/linkedin`;
      const { data, error } = await supabase.functions.invoke<LinkedInAuthorizeResponse>(
        "relay-social-linkedin-authorize",
        { body: { organization_id: organizationId, redirect_uri: redirectUri } },
      );
      if (error) throw error;
      if (!data?.authorization_url) {
        throw new Error("relay-social-linkedin-authorize returned no authorization URL");
      }
      return data;
    },
    onSuccess: (data) => {
      window.location.assign(data.authorization_url);
    },
  });
}
